import { Button, Label, Spinner, TextInput } from 'flowbite-react';
import React, { useRef, useState } from 'react';
import Container from '../shared/Container';
import { formatCareerPathType, validateEmailAddress } from 'shared-utils';
import { RegisterUserInput, UserCareerPathType, UserExperienceLevelType } from 'types';
import Select from 'react-select';
import moment from 'moment';
import AssessmentExpired from './AssessmentExpired';

function isDeadlinePassed(deadline: Date) {
	const deadlineMoment = moment(deadline);
	return deadlineMoment.isBefore(moment());
}

interface GuestUserEmailProps {
	showSendOTPScreen: boolean;
	setShowSendOTPScreen: React.Dispatch<React.SetStateAction<boolean>>;
	handleSendOtp: () => void;
	guestUserEmail: string;
	setGuestUserEmail: React.Dispatch<React.SetStateAction<string>>;
	isLoading: boolean;
	companyName?: string;
	createdByName?: string;
	careerpath?: string;
	guestUserName: string;
	setGuestUserName: React.Dispatch<React.SetStateAction<string>>;
	guestUserExperience: UserExperienceLevelType | undefined;
	setGuestUserExperience: React.Dispatch<React.SetStateAction<UserExperienceLevelType | undefined>>;
	hiringAssessmentDeadline: Date | null;
}

const GuestUserEmail: React.FC<GuestUserEmailProps> = ({
	companyName,
	showSendOTPScreen,
	setShowSendOTPScreen,
	handleSendOtp,
	guestUserEmail,
	guestUserName,
	setGuestUserName,
	guestUserExperience,
	setGuestUserEmail,
	setGuestUserExperience,
	createdByName,
	careerpath,
	isLoading,
	hiringAssessmentDeadline,
}) => {
	// const [isLoading, setIsLoading] = useState<boolean>(false);
	const [email, setEmail] = useState<string>('');

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		// setIsLoading(true);
		await handleSendOtp();
		// setIsLoading(false);
	};

	const experienceOptions = [
		{ label: 'Final Year Student', value: UserExperienceLevelType.FINAL_YEAR_STUDENT, exp: 0 },
		{ label: 'Graduated in 2024', value: UserExperienceLevelType.FRESHER, exp: 0 },
		{ label: '1-2 years experience', value: UserExperienceLevelType.OneToTwoYears, exp: 1 },
		{ label: '2+ years experience', value: UserExperienceLevelType.TwoPlusYears, exp: 2 },
	];
	const validateForm = () => {
		return validateEmailAddress(guestUserEmail) && guestUserName && guestUserExperience;
	};

	if (isDeadlinePassed(hiringAssessmentDeadline as Date)) {
		return <AssessmentExpired expiredDate={moment(hiringAssessmentDeadline).format('h:mma, DD MMMM YYYY') as string} />;
	}

	return (
		<Container className="flex items-center justify-center">
			<div className="px-4 max-w-[560px] mx-auto rounded-[8px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.12)] bg-[#FFF] gap-[16px] justify-center">
				<div className="text-center content-center pt-5 md:pt-[36px] max-w-[492px] mx-auto">
					<h6 className="font-inter text-base md:text-xl font-[700] leading-[24px] text-[#000]">
						Great news! <br /> You've been invited to take an assessment.
					</h6>
				</div>
				<div className="text-center content-center w-[100%] pt-3 md:pt-4 pb-4 md:pb-[26px] max-w-[480px] mx-auto ">
					<p className="font-inter text-sm md:text-base font-[500] inline-block text-[#28293D] max-w-[420px]">
						<strong className="capitalize">{createdByName}</strong> from{' '}
						<strong className="capitalize">{companyName}</strong> has invited you to take the LevelUp assessment for{' '}
						{careerpath ? formatCareerPathType(careerpath as UserCareerPathType) : ''} role.
					</p>

					<p className="font-inter text-sm md:text-base font-[500] inline-block text-[#28293D] text-center max-w-80">
						{hiringAssessmentDeadline ? (
							<>
								Please complete the assessment before{' '}
								<strong>{moment(hiringAssessmentDeadline).format('h:mmA')}</strong> on{' '}
								<strong>{moment(hiringAssessmentDeadline).format('MMMM Do, YYYY')}</strong>.
							</>
						) : null}
					</p>
				</div>
				<form className={'flex flex-col pb-5 mx-auto'} onSubmit={handleSubmit}>
					<div className="gap-[8px] max-w-[420px] mx-auto w-full">
						<div className="mb-2 block">
							<Label className={'dark:text-white text-[#111928] font-inter'} htmlFor="name" value="Name " />
							<span className="text-red-600">*</span>
						</div>
						<TextInput
							sizing="lg"
							id="name"
							type="text"
							placeholder="Your Name"
							required={true}
							value={guestUserName}
							onChange={e => setGuestUserName && setGuestUserName(e.target?.value)}
							style={{
								color: 'black',
								border: '1px solid #D1D5DB',
								backgroundColor: 'white',
							}}
						/>
					</div>
					<div className="gap-[8px] max-w-[420px] mx-auto w-full">
						<div className="mb-2">
							<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="phone" value="Email " />
							<span className="text-red-600">*</span>
						</div>

						<TextInput
							sizing="lg"
							name="email"
							type="email"
							style={{
								color: 'black',
								border: '1px solid #D1D5DB',
								backgroundColor:
									guestUserEmail && !validateEmailAddress(guestUserEmail) ? 'rgb(253 242 242 / 1)' : 'white',
							}}
							color={guestUserEmail && !validateEmailAddress(guestUserEmail) ? 'failure' : 'gray'}
							helperText={
								guestUserEmail && !validateEmailAddress(guestUserEmail) ? 'Please enter a valid email address' : null
							}
							className=" bg-white dark:text-white dark:border-[#4B5563] rounded-[8px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
							placeholder="name@gmail.com"
							value={guestUserEmail}
							onChange={e => setGuestUserEmail(e.target?.value?.toLowerCase())}
						/>
					</div>
					<div className="gap-[8px] max-w-[420px] mx-auto w-full">
						<div className="mb-2 block">
							<Label
								className={'dark:text-[#fff] text-[#111928] font-inter'}
								htmlFor="experience"
								value="Experience "
							/>
							<span className="text-red-600">*</span>
						</div>

						<div>
							<Select
								id="experience"
								required
								isClearable
								options={experienceOptions}
								placeholder="Select Experience"
								classNamePrefix="flowbite-select"
								value={experienceOptions?.find(item => item.value === guestUserExperience)}
								onChange={option => setGuestUserExperience && setGuestUserExperience(option?.value)}
							/>
						</div>
					</div>
					<div className="flex justify-center pt-[26px]">
						<Button
							color="purplePlaced"
							onClick={handleSendOtp}
							disabled={isLoading || !validateForm()}
							className="items-center gap-[8px] font-[500] leading-[21px]"
						>
							{isLoading ? <Spinner /> : 'Verify Email'}
						</Button>
					</div>
				</form>
			</div>
		</Container>
	);
};

export default GuestUserEmail;
